import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/MasterData`;

// ============================================================
// Servicio de Master Data
// Controller: MasterDataController
// Ruta base: /api/MasterData
// Autenticación: JWT Bearer (todos los endpoints)
// Total endpoints: 19
// ============================================================

// ────────────────────────────────────────────────────────────
// SECCIÓN 1: Post-Login
// ────────────────────────────────────────────────────────────

/**
 * Obtiene información completa del empleado y sus roles después del login.
 * Si tiene múltiples roles, retorna múltiples filas (una por rol).
 *
 * @param {number} employeeId - ID del empleado
 *
 * Ejemplo de llamada:
 * GET /api/MasterData/employee-info/74
 *
 * Respuesta exitosa (200) - Un rol:
 * [
 *   {
 *     "employeeId": 74,
 *     "firstName": "Ricot",
 *     "lastName": "Jean Simon",
 *     "mail": "admin@santeplusmed.net",
 *     "businessEmail": "ricot@santeplusmedical.com",
 *     "jobPositionId": 3,
 *     "jobPositionName": "CEO",
 *     "photo": null,
 *     "roleId": 1,
 *     "roleName": "Admin",
 *     "roleKey": "admin",
 *     "mfaEnabled": true
 *   }
 * ]
 *
 * Respuesta exitosa (200) - Múltiples roles:
 * [
 *   { ...employeeData, "roleId": 2, "roleName": "PCP", "roleKey": "pcp" },
 *   { ...employeeData, "roleId": 5, "roleName": "Care Management", "roleKey": "care_management" }
 * ]
 *
 * Si no tiene roles:
 * [{ ...employeeData, "roleId": null, "roleName": null, "roleKey": null }]
 *
 * Campo `mfaEnabled` (bool): true si el usuario ya enroló 2FA y está activo.
 * Si es false, el frontend debe forzar el enrollment antes de permitir el uso
 * del sistema (requisito HIPAA — ver ProtectedRoute + /mfa-required).
 */
export const getEmployeeInfo = async (employeeId) => {
  try {
    const response = await axios.get(
      `${API_URL}/employee-info/${employeeId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getEmployeeInfo:", error);
    throw error;
  }
};

/**
 * Obtiene todas las locations asignadas a un empleado, ordenadas por primaria primero.
 *
 * @param {number} employeeId - ID del empleado
 *
 * Ejemplo de llamada:
 * GET /api/MasterData/employee-locations/74
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "locationId": 1,
 *     "centerKey": "fort_myers",
 *     "medicalCenter": "Fort Myers",
 *     "isPrimary": true,
 *     "addressOffice": "3822 BROADWAY STE A, Fort Myers, FL - 33901-8148, Lee"
 *   },
 *   {
 *     "locationId": 5,
 *     "centerKey": "lake_worth",
 *     "medicalCenter": "Lake Worth",
 *     "isPrimary": false,
 *     "addressOffice": "3155 LAKE WORTH RD, Palm Springs, FL - 33461-6917, Palm Beach"
 *   }
 * ]
 */
export const getEmployeeLocations = async (employeeId) => {
  try {
    const response = await axios.get(
      `${API_URL}/employee-locations/${employeeId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getEmployeeLocations:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 2: Locations
// ────────────────────────────────────────────────────────────

/**
 * Crea una nueva location. El sistema genera automáticamente centerKey a partir de medicalCenter.
 *
 * @param {Object} data
 * @param {string} data.medicalCenter - Nombre de la location (obligatorio)
 * @param {boolean} data.clinical - true = clínica, false = administrativa (obligatorio)
 * @param {number|null} [data.managerId] - ID del manager
 * @param {number|null} [data.latitude] - Latitud geográfica
 * @param {number|null} [data.longitude] - Longitud geográfica
 * @param {string|null} [data.officeEmail] - Email de la oficina
 * @param {string|null} [data.addressOffice] - Dirección física
 *
 * Ejemplo de request:
 * {
 *   "medicalCenter": "West Palm Beach",
 *   "clinical": true,
 *   "managerId": 68,
 *   "latitude": 26.715342,
 *   "longitude": -80.053375,
 *   "officeEmail": "wpb@santeplusmedical.com",
 *   "addressOffice": "123 Main St, West Palm Beach, FL 33401"
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Location created successfully", "locationId": 12, "centerKey": "west_palm_beach" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Medical center name is required" }
 * { "result": 2, "message": "A location with key \"west_palm_beach\" already exists" }
 */
export const createLocation = async (data) => {
  try {
    const response = await axios.post(
      `${API_URL}/locations`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en createLocation:", error);
    throw error;
  }
};

/**
 * Actualiza datos de una location existente. Solo se modifican los campos enviados (null = no cambiar).
 * centerKey es inmutable.
 *
 * @param {Object} data
 * @param {number} data.locationId - ID de la location (obligatorio)
 * @param {string|null} [data.medicalCenter] - Nombre (null = no cambiar)
 * @param {boolean|null} [data.clinical] - Tipo (null = no cambiar)
 * @param {number|null} [data.managerId] - Manager (null = no cambiar)
 * @param {number|null} [data.latitude] - Latitud (null = no cambiar)
 * @param {number|null} [data.longitude] - Longitud (null = no cambiar)
 * @param {string|null} [data.officeEmail] - Email (null = no cambiar)
 * @param {string|null} [data.addressOffice] - Dirección (null = no cambiar)
 *
 * Ejemplo de request:
 * { "locationId": 12, "medicalCenter": "West Palm Beach Main", "managerId": 74 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Location updated successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Location not found or inactive" }
 * { "result": 2, "message": "Cannot change location to non-clinical: active PCPs are assigned" }
 */
export const updateLocation = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/locations`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en updateLocation:", error);
    throw error;
  }
};

/**
 * Desactiva una location y todas sus asignaciones de empleados en cascada.
 *
 * @param {number} locationId - ID de la location
 *
 * Ejemplo de request:
 * { "locationId": 12 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Location deactivated successfully (3 assignments deactivated)" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Location not found or already inactive" }
 */
export const deactivateLocation = async (locationId) => {
  try {
    const response = await axios.put(
      `${API_URL}/locations/deactivate`,
      { locationId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en deactivateLocation:", error);
    throw error;
  }
};

/**
 * Consulta locations con filtros opcionales.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.locationId] - Filtrar por ID específico
 * @param {boolean|null} [filters.clinical] - true = clínicas, false = administrativas, null = todas
 * @param {number|null} [filters.status] - 1 = activas, 0 = inactivas, null = todas
 *
 * Ejemplos de llamada:
 * GET /api/MasterData/locations
 * GET /api/MasterData/locations?clinical=true&status=1
 * GET /api/MasterData/locations?locationId=1
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "locationId": 1, "centerKey": "fort_myers", "medicalCenter": "Fort Myers",
 *     "managerId": 68, "managerName": "Samuel Admin",
 *     "latitude": 26.640628, "longitude": -81.872307,
 *     "officeEmail": "fm@santeplusmedical.com",
 *     "addressOffice": "3822 BROADWAY STE A, Fort Myers, FL - 33901",
 *     "clinical": true, "status": 1,
 *     "activeEmployees": 15, "activePcps": 5,
 *     "createDate": "2026-01-15T00:00:00", "updateDate": null
 *   }
 * ]
 */
export const getLocations = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.locationId != null) params.append("locationId", filters.locationId);
    if (filters.clinical != null) params.append("clinical", filters.clinical);
    if (filters.status != null) params.append("status", filters.status);

    const response = await axios.get(
      `${API_URL}/locations${params.toString() ? `?${params}` : ""}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getLocations:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 3: Employees
// ────────────────────────────────────────────────────────────

/**
 * Crea un nuevo empleado y lo asigna a al menos una location.
 *
 * @param {Object} data
 * @param {string} data.firstName - Nombre (obligatorio)
 * @param {string} data.lastName - Apellido (obligatorio)
 * @param {number} data.jobPositionId - ID del cargo (obligatorio)
 * @param {string} data.admission - Fecha de admisión YYYY-MM-DD (obligatorio)
 * @param {string} data.locations - JSON string de locations (obligatorio)
 *   Formato: '[{"NUM_ID_LOCATION":1,"BIT_PRIMARY":1},{"NUM_ID_LOCATION":5,"BIT_PRIMARY":0}]'
 * @param {string|null} [data.mail] - Email personal (debe ser único)
 * @param {string|null} [data.businessEmail] - Email corporativo
 * @param {string|null} [data.officePhone] - Teléfono de oficina
 * @param {string|null} [data.extension] - Extensión
 * @param {string|null} [data.phone2] - Teléfono secundario
 * @param {number|null} [data.managerId] - ID del manager
 * @param {string|null} [data.photo] - URL de la foto
 * @param {string|null} [data.gender] - Género ("M" / "F")
 * @param {string|null} [data.dob] - Fecha de nacimiento YYYY-MM-DD
 *
 * Ejemplo de request:
 * {
 *   "firstName": "John", "lastName": "Smith", "jobPositionId": 22,
 *   "admission": "2026-02-15", "mail": "jsmith@example.com",
 *   "locations": "[{\"NUM_ID_LOCATION\":1,\"BIT_PRIMARY\":1}]"
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Employee created successfully", "employeeId": 89 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "First name is required" }
 * { "result": 2, "message": "Last name is required" }
 * { "result": 3, "message": "Job position is required" }
 * { "result": 4, "message": "Admission date is required" }
 * { "result": 5, "message": "Email already exists" }
 * { "result": 6, "message": "Job position not found or inactive" }
 * { "result": 7, "message": "Employee must be assigned to at least one location" }
 */
export const createEmployee = async (data) => {
  try {
    const response = await axios.post(
      `${API_URL}/employees`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en createEmployee:", error);
    throw error;
  }
};

/**
 * Actualiza datos de un empleado existente. Solo se modifican los campos enviados (null = no cambiar).
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del empleado (obligatorio)
 * @param {string|null} [data.firstName] - Nombre
 * @param {string|null} [data.lastName] - Apellido
 * @param {number|null} [data.jobPositionId] - ID del cargo
 * @param {string|null} [data.admission] - Fecha de admisión
 * @param {string|null} [data.mail] - Email personal
 * @param {string|null} [data.businessEmail] - Email corporativo
 * @param {string|null} [data.officePhone] - Teléfono
 * @param {string|null} [data.extension] - Extensión
 * @param {string|null} [data.phone2] - Teléfono 2
 * @param {number|null} [data.managerId] - Manager
 * @param {string|null} [data.photo] - Foto URL
 * @param {string|null} [data.gender] - Género
 * @param {string|null} [data.dob] - Fecha de nacimiento
 *
 * Ejemplo de request:
 * { "employeeId": 89, "businessEmail": "jsmith@santeplusmedical.com", "officePhone": "239-555-0200" }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Employee updated successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found or inactive" }
 * { "result": 2, "message": "Email already exists" }
 * { "result": 3, "message": "Job position not found or inactive" }
 */
export const updateEmployee = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/employees`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en updateEmployee:", error);
    throw error;
  }
};

/**
 * Desactiva un empleado y todos sus registros en cascada (usuario, sesión, roles, locations, PCP configs).
 *
 * @param {number} employeeId - ID del empleado
 *
 * Ejemplo de request:
 * { "employeeId": 89 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Employee deactivated successfully (1 user, 1 session, 2 roles, 3 locations, 1 PCP configs)" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found or already inactive" }
 */
export const deactivateEmployee = async (employeeId) => {
  try {
    const response = await axios.put(
      `${API_URL}/employees/deactivate`,
      { employeeId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en deactivateEmployee:", error);
    throw error;
  }
};

/**
 * Consulta empleados con filtros opcionales.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.employeeId] - Empleado específico
 * @param {number|null} [filters.jobPositionId] - Filtrar por cargo
 * @param {number|null} [filters.locationId] - Empleados de una location
 * @param {number|null} [filters.status] - 1 = activos, 0 = inactivos, null = todos
 * @param {string|null} [filters.search] - Búsqueda por nombre/email (LIKE)
 *
 * Ejemplos de llamada:
 * GET /api/MasterData/employees
 * GET /api/MasterData/employees?status=1
 * GET /api/MasterData/employees?locationId=1&status=1
 * GET /api/MasterData/employees?search=john
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeeId": 89, "firstName": "John", "lastName": "Smith",
 *     "mail": "jsmith@example.com", "businessEmail": "jsmith@santeplusmedical.com",
 *     "jobPositionId": 22, "jobPositionName": "PCP",
 *     "admission": "2026-02-15T00:00:00",
 *     "officePhone": "239-555-0200", "extension": "101", "phone2": null,
 *     "managerId": 68, "managerName": "Samuel Admin",
 *     "photo": null, "gender": "M", "dob": "1985-06-20T00:00:00",
 *     "status": 1, "hasUser": true, "isPcp": true,
 *     "activeLocations": 2, "activeRoles": 1,
 *     "createDate": "2026-02-15T10:30:00", "updateDate": null
 *   }
 * ]
 */
export const getEmployees = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.employeeId != null) params.append("employeeId", filters.employeeId);
    if (filters.jobPositionId != null) params.append("jobPositionId", filters.jobPositionId);
    if (filters.locationId != null) params.append("locationId", filters.locationId);
    if (filters.status != null) params.append("status", filters.status);
    if (filters.search != null) params.append("search", filters.search);

    const response = await axios.get(
      `${API_URL}/employees${params.toString() ? `?${params}` : ""}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getEmployees:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 4: Employee Locations
// ────────────────────────────────────────────────────────────

/**
 * Asigna un empleado a una location. Si ya existe inactivo lo reactiva, si existe activo actualiza primary.
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del empleado (obligatorio)
 * @param {number} data.locationId - ID de la location (obligatorio)
 * @param {boolean} [data.primary=false] - true = primaria, false = secundaria
 *
 * Ejemplo de request:
 * { "employeeId": 89, "locationId": 3, "primary": false }
 *
 * Respuesta exitosa (200) - Nueva asignación:
 * { "result": 0, "message": "Employee assigned to location successfully", "employeeLocationId": 15 }
 *
 * Respuesta exitosa (200) - Reactivación:
 * { "result": 0, "message": "Employee location reactivated successfully", "employeeLocationId": 15 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found or inactive" }
 * { "result": 2, "message": "Location not found or inactive" }
 */
export const assignEmployeeLocation = async (data) => {
  try {
    const response = await axios.post(
      `${API_URL}/employee-locations/assign`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en assignEmployeeLocation:", error);
    throw error;
  }
};

/**
 * Desactiva la asignación de un empleado a una location. No permite quitar la última location.
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del empleado (obligatorio)
 * @param {number} data.locationId - ID de la location (obligatorio)
 *
 * Ejemplo de request:
 * { "employeeId": 89, "locationId": 3 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Employee location removed successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Assignment not found or already inactive" }
 * { "result": 2, "message": "Cannot remove last location. Employee must have at least one active location" }
 */
export const removeEmployeeLocation = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/employee-locations/remove`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en removeEmployeeLocation:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 5: PCP Config
// ────────────────────────────────────────────────────────────

/**
 * Crea configuración PCP para un empleado en una location.
 *
 * @param {Object} data
 * @param {number} data.employeePcpId - ID del empleado (obligatorio)
 * @param {number} data.locationId - ID de la location (obligatorio)
 * @param {boolean} data.highRisk - Acepta pacientes high risk (obligatorio)
 * @param {boolean} data.highCost - Acepta pacientes high cost (obligatorio)
 * @param {boolean} data.medicare - Acepta pacientes Medicare (obligatorio)
 * @param {boolean} data.critical - Acepta pacientes críticos (obligatorio)
 * @param {number|null} [data.employeeSupportId] - PCP de respaldo
 * @param {string|null} [data.title] - Título ("MD", "APRN", etc.)
 * @param {number|null} [data.ageMin] - Edad mínima (ambos null = any age)
 * @param {number|null} [data.ageMax] - Edad máxima (ambos null = any age)
 * @param {number|null} [data.maxPatient] - Capacidad máxima (null = sin límite)
 * @param {number|null} [data.daysWork] - Días que trabaja por semana (0-7)
 *
 * Ejemplo de request:
 * {
 *   "employeePcpId": 89, "locationId": 1, "title": "MD",
 *   "highRisk": true, "highCost": true, "medicare": true, "critical": false,
 *   "ageMin": 50, "ageMax": 120, "maxPatient": 3000, "daysWork": 5
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "PCP config created successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found or inactive" }
 * { "result": 2, "message": "Location not found, inactive, or not clinical" }
 * { "result": 3, "message": "Active PCP config already exists for this employee and location" }
 * { "result": 4, "message": "Age range: both MIN and MAX must be NULL or both must have values" }
 * { "result": 5, "message": "Support PCP not found or inactive" }
 */
export const createPcpConfig = async (data) => {
  try {
    const response = await axios.post(
      `${API_URL}/pcp-config`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en createPcpConfig:", error);
    throw error;
  }
};

/**
 * Actualiza configuración PCP existente. Solo se modifican los campos enviados (null = no cambiar).
 *
 * @param {Object} data
 * @param {number} data.employeePcpId - ID del empleado (obligatorio, identifica registro)
 * @param {number} data.locationId - ID de la location (obligatorio, identifica registro)
 * @param {number|null} [data.employeeSupportId] - PCP de respaldo (null = no cambiar)
 * @param {string|null} [data.title] - Título (null = no cambiar)
 * @param {boolean|null} [data.highRisk] - (null = no cambiar)
 * @param {boolean|null} [data.highCost] - (null = no cambiar)
 * @param {boolean|null} [data.medicare] - (null = no cambiar)
 * @param {boolean|null} [data.critical] - (null = no cambiar)
 * @param {number|null} [data.ageMin] - (null = no cambiar)
 * @param {number|null} [data.ageMax] - (null = no cambiar)
 * @param {number|null} [data.maxPatient] - (null = no cambiar)
 * @param {number|null} [data.daysWork] - (null = no cambiar)
 *
 * Ejemplo de request:
 * { "employeePcpId": 89, "locationId": 1, "employeeSupportId": 27, "critical": true, "maxPatient": 5000 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "PCP config updated successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "PCP config not found or inactive" }
 * { "result": 2, "message": "Age range: both MIN and MAX must be NULL or both must have values" }
 * { "result": 3, "message": "Support PCP not found or inactive" }
 */
export const updatePcpConfig = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/pcp-config`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en updatePcpConfig:", error);
    throw error;
  }
};

/**
 * Desactiva configuración PCP. Permite desactivar aunque tenga pacientes (retorna warnings).
 *
 * @param {Object} data
 * @param {number} data.employeePcpId - ID del empleado (obligatorio)
 * @param {number} data.locationId - ID de la location (obligatorio)
 *
 * Ejemplo de request:
 * { "employeePcpId": 89, "locationId": 1 }
 *
 * Respuesta exitosa SIN warning (200):
 * { "result": 0, "message": "PCP config deactivated successfully", "warning": false, "patientsAffected": 0, "remainingPcps": 3 }
 *
 * Respuesta exitosa CON warning (200):
 * { "result": 0, "message": "WARNING: This PCP has 45 assigned patients...", "warning": true, "patientsAffected": 45, "remainingPcps": 2 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "PCP config not found or already inactive" }
 */
export const deactivatePcpConfig = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/pcp-config/deactivate`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en deactivatePcpConfig:", error);
    throw error;
  }
};

/**
 * Reactiva configuración PCP desactivada. No recupera pacientes anteriores.
 *
 * @param {Object} data
 * @param {number} data.employeePcpId - ID del empleado (obligatorio)
 * @param {number} data.locationId - ID de la location (obligatorio)
 *
 * Ejemplo de request:
 * { "employeePcpId": 89, "locationId": 1 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "PCP config reactivated successfully" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "PCP config not found or already active" }
 * { "result": 2, "message": "Employee not found or inactive" }
 * { "result": 3, "message": "Location not found, inactive, or not clinical" }
 */
export const reactivatePcpConfig = async (data) => {
  try {
    const response = await axios.put(
      `${API_URL}/pcp-config/reactivate`,
      data,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en reactivatePcpConfig:", error);
    throw error;
  }
};

/**
 * Consulta PCPs con sus configuraciones completas y estadísticas.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.employeePcpId] - PCP específico
 * @param {number|null} [filters.locationId] - PCPs de una location
 * @param {boolean|null} [filters.highRisk] - Solo PCPs que aceptan high risk
 * @param {boolean|null} [filters.highCost] - Solo PCPs que aceptan high cost
 * @param {boolean|null} [filters.medicare] - Solo PCPs que aceptan Medicare
 * @param {boolean|null} [filters.critical] - Solo PCPs que aceptan critical
 * @param {number|null} [filters.status] - 1 = activos, 0 = inactivos, null = todos
 *
 * Ejemplos de llamada:
 * GET /api/MasterData/pcps
 * GET /api/MasterData/pcps?locationId=1&status=1
 * GET /api/MasterData/pcps?highRisk=true&medicare=true
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeePcpId": 27, "pcpFullName": "Reynaldo Pezzotti", "title": "MD",
 *     "jobPositionId": 22, "jobPositionName": "PCP",
 *     "locationId": 1, "medicalCenter": "Fort Myers", "centerKey": "fort_myers",
 *     "highRisk": true, "highCost": true, "medicare": true, "critical": false,
 *     "ageMin": 50, "ageMax": 120, "ageRange": "50-120",
 *     "maxPatient": 3000, "daysWork": 1,
 *     "patientsAssigned": 0, "capacity": 3000, "capacityPercentage": 0.0,
 *     "available": true, "status": 1,
 *     "createDate": "2026-01-20T00:00:00", "updateDate": null
 *   }
 * ]
 */
export const getPcps = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.employeePcpId != null) params.append("employeePcpId", filters.employeePcpId);
    if (filters.locationId != null) params.append("locationId", filters.locationId);
    if (filters.highRisk != null) params.append("highRisk", filters.highRisk);
    if (filters.highCost != null) params.append("highCost", filters.highCost);
    if (filters.medicare != null) params.append("medicare", filters.medicare);
    if (filters.critical != null) params.append("critical", filters.critical);
    if (filters.status != null) params.append("status", filters.status);

    const response = await axios.get(
      `${API_URL}/pcps${params.toString() ? `?${params}` : ""}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getPcps:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 6: Roles & Modules
// ────────────────────────────────────────────────────────────

/**
 * Obtiene todos los módulos del sistema con los permisos CRUD del rol seleccionado.
 * Retorna TODOS los módulos activos: los que tienen permiso con flags en true,
 * y los que no con flags en false.
 *
 * @param {number} employeeId - ID del empleado (obligatorio)
 * @param {number} roleId - ID del rol seleccionado (obligatorio)
 *
 * Ejemplo de llamada:
 * GET /api/MasterData/role-modules?employeeId=74&roleId=1
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "moduleId": 1, "moduleName": "Dashboard", "moduleKey": "dashboard",
 *     "canCreate": false, "canRead": true, "canUpdate": false, "canDelete": false,
 *     "hasAccess": true
 *   },
 *   {
 *     "moduleId": 2, "moduleName": "Employee Management", "moduleKey": "employee_management",
 *     "canCreate": true, "canRead": true, "canUpdate": true, "canDelete": true,
 *     "hasAccess": true
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "User not found or inactive" }
 * { "result": 2, "message": "Role not found or inactive" }
 * { "result": 3, "message": "Role not assigned to this user" }
 */
export const getRoleModules = async (employeeId, roleId) => {
  try {
    const response = await axios.get(
      `${API_URL}/role-modules?employeeId=${employeeId}&roleId=${roleId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en getRoleModules:", error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 7: Placeholders (SPs pendientes en backend)
// ────────────────────────────────────────────────────────────

/**
 * Obtiene el catálogo de job positions.
 *
 * Obtiene la lista de cargos (job positions) del sistema.
 *
 * PLACEHOLDER — SP_GET_JOB_POSITIONS no existe aún en el backend.
 * Datos alineados con TAB_JOB_POSITIONS en DB_MCM_SANTEPLUS.
 *
 * Respuesta esperada cuando el SP exista:
 * GET /api/MasterData/job-positions
 * [
 *   { "id": 1, "name": "APRN", "key": "aprn" },
 *   { "id": 2, "name": "Assistant", "key": "assistant" },
 *   ...
 * ]
 */
export const getJobPositions = async () => {
  // TODO: Replace with real endpoint when SP_GET_JOB_POSITIONS is built
  // Datos alineados con TAB_JOB_POSITIONS (2026-04-10)
  return [
    { id: 1, name: "APRN", key: "aprn" },
    { id: 2, name: "Assistant", key: "assistant" },
    { id: 3, name: "CEO", key: "ceo" },
    { id: 4, name: "CFO", key: "cfo" },
    { id: 5, name: "COO", key: "coo" },
    { id: 6, name: "COO Assistant", key: "coo_assistant" },
    { id: 7, name: "Call Center Manager", key: "call_center_manager" },
    { id: 8, name: "Call Center Rep", key: "call_center_rep" },
    { id: 9, name: "Care Coordinator", key: "care_coordinator" },
    { id: 10, name: "Care Manager", key: "care_manager" },
    { id: 11, name: "Center Manager", key: "center_manager" },
    { id: 12, name: "Coder", key: "coder" },
    { id: 13, name: "East Coast Manager", key: "east_coast_manager" },
    { id: 14, name: "Executive Assistant", key: "executive_assistant" },
    { id: 15, name: "Front Desk", key: "front_desk" },
    { id: 16, name: "Head Care Manager", key: "head_care_manager" },
    { id: 17, name: "Lab Tech", key: "lab_tech" },
    { id: 18, name: "Lead Care Coordinator", key: "lead_care_coordinator" },
    { id: 19, name: "Medical Assistant", key: "medical_assistant" },
    { id: 20, name: "Medical Director", key: "medical_director" },
    { id: 21, name: "Medical Receptionist", key: "medical_receptionist" },
    { id: 22, name: "PCP", key: "pcp" },
    { id: 23, name: "Psychologist", key: "psychologist" },
    { id: 24, name: "Senior Health Administrator", key: "senior_health_administrator" },
    { id: 25, name: "West Coast Manager", key: "west_coast_manager" },
    { id: 26, name: "System Admin", key: "System_Admin" },
  ];
};

/**
 * Obtiene la lista de usuarios del sistema.
 *
 * PLACEHOLDER — SP_GET_USERS no existe aún en el backend.
 * Workaround: llama getEmployees y filtra los que tienen cuenta de usuario (hasUser = true).
 *
 * Respuesta esperada cuando el SP exista:
 * GET /api/MasterData/users
 * [
 *   {
 *     "employeeId": 74, "firstName": "Ricot", "lastName": "Jean Simon",
 *     "mail": "admin@santeplusmed.net", "isActive": true, "isLocked": false,
 *     "lastAccessDate": "2026-02-16T10:00:00"
 *   }
 * ]
 */
export const getUsers = async () => {
  // TODO: Replace with real endpoint when SP_GET_USERS is built
  const employees = await getEmployees({ status: 1 });
  return employees
    .filter((e) => e.hasUser)
    .map((e) => ({
      ...e,
      isActive: e.status === 1,
      isLocked: false,
      lastAccessDate: null,
    }));
};

/**
 * Obtiene el catálogo de roles del sistema.
 *
 * PLACEHOLDER — SP_GET_ROLES no existe aún en el backend.
 * Retorna datos estáticos hasta que el SP sea implementado.
 *
 * Respuesta esperada cuando el SP exista:
 * GET /api/MasterData/roles
 * [
 *   { "id": 1, "name": "Admin", "key": "admin" },
 *   ...
 * ]
 */
export const getRoles = async () => {
  // TODO: Replace with real endpoint when SP_GET_ROLES is built
  return [
    { id: 1, name: "Admin", key: "admin" },
    { id: 2, name: "PCP", key: "pcp" },
    { id: 3, name: "Care Management", key: "care_management" },
    { id: 4, name: "CEO", key: "ceo" },
  ];
};

// ────────────────────────────────────────────────────────────
// SECCIÓN 8: Statuses
// ────────────────────────────────────────────────────────────

/**
 * Consulta el catálogo transversal de estados por grupo.
 * Cada módulo tiene sus propios estados y existen estados globales (Active/Inactive)
 * que aplican a todas las tablas.
 *
 * @param {string|null} [statusGroup] - Grupo de estados: "global", "employees", "locations", null = todos
 * @param {boolean|null} [includeGlobal=true] - true = incluir estados globales junto con los del grupo
 *
 * Ejemplos de llamada:
 * GET /api/MasterData/statuses
 * GET /api/MasterData/statuses?statusGroup=employees
 * GET /api/MasterData/statuses?statusGroup=locations&includeGlobal=false
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "statusId": 1, "statusGroup": "global", "statusKey": "active",
 *     "statusName": "Active", "description": "Record is active and operational",
 *     "displayOrder": 1
 *   },
 *   {
 *     "statusId": 0, "statusGroup": "global", "statusKey": "inactive",
 *     "statusName": "Inactive", "description": "Record is deactivated",
 *     "displayOrder": 2
 *   }
 * ]
 */
export const getStatuses = async (statusGroup = null, includeGlobal = null) => {
  try {
    const params = new URLSearchParams();
    if (statusGroup != null) params.append("statusGroup", statusGroup);
    if (includeGlobal != null) params.append("includeGlobal", includeGlobal);

    const response = await axios.get(
      `${API_URL}/statuses${params.toString() ? `?${params}` : ""}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en getStatuses:", error);
    throw error;
  }
};
